import React, { useState, useRef, useEffect, useCallback } from "react";
import { useInView } from "react-intersection-observer";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";

import { ReactComponent as ImageIcon } from "../../../assets/icons/image.svg";
import { ReactComponent as VideoRecorderIcon } from "../../../assets/icons/video-recorder.svg";
import { ReactComponent as FailedIcon } from "../../../assets/icons/failed.svg";

import LottieIcon from "../../../components/LottieIcon/LottieIcon";
import checkboxAnimation from "../../../assets/animations/radioButton.json";
import settingsAnimation from "../../../assets/animations/settings.json";
import generatingAnimation from "../../../assets/animations/onLoad.json";
import { selectAutoPlay } from "../../../store/slices/common/slice";
import { useSelector } from "react-redux";

import { formatModelName, isImageOutput } from "../../../utils/utils";
import {
  BLEND_MODEL,
  COLLECTION_STATUS,
  MEDIA_TYPE,
  QWEN_Z_THREE_INPUT_IMAGES,
  QWEN_Z_TWO_INPUT_IMAGES,
  VIDEO_RESOLUTION_TO_TIME,
} from "../../../utils/constants";
import {
  regenerateBlendCollection,
  regenerateCollection,
  regenerateImageCollection,
} from "../../../store/slices/model/asyncThunks";

import {
  Date as DateLabel,
  ModelName,
  VideoElement,
  VideoFooter,
  RunningText,
  RunningTitle,
  RunningMessage,
  DotsSpan,
  ShimmerTitleWrapper,
  ShimmerTitle,
  ShimmerText,
  FreeMessageWrapper,
  LoadingSpinnerBlur,
  CollectionContainer,
  CollectionThumbnail,
  ImageElement,
  IconWrapper,
  CheckboxContainer,
  UpgradeButton,
  FailedOverlay,
  FailedContent,
  FailedIconWrapper,
  FailedModelName,
  FailedTitle,
  FailedMessage,
  RetryButton,
  LoadingImage,
} from "./CollectionItem.styles";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const LOTTIE_GENERATING_STYLE = {
  zIndex: 1,
  marginBottom: -12,
  marginTop: -40,
  filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))",
};

const DOT_INTERVAL = 500;
const DOT_FRAMES = ["", ".", "..", "..."];
const LOADING_TEXT_TICKS = DOT_FRAMES.length * 2;

const useLoadingTitle = (texts) => {
  const list = Array.isArray(texts) ? texts.filter(Boolean) : [];
  const length = list.length;
  const [state, setState] = useState(() => ({
    index: Math.floor(Math.random() * 1000),
    tick: 0,
  }));

  useEffect(() => {
    const interval = setInterval(() => {
      setState((prev) => {
        const tick = (prev.tick + 1) % LOADING_TEXT_TICKS;

        return {
          tick,
          index: tick === 0 && length ? prev.index + 1 : prev.index,
        };
      });
    }, DOT_INTERVAL);

    return () => clearInterval(interval);
  }, [length]);

  return {
    dots: DOT_FRAMES[state.tick % DOT_FRAMES.length],
    loadingText: length ? list[state.index % length] : null,
  };
};

const capitalizeFirstLetter = (text) =>
  typeof text === "string"
    ? text.replace(/^(\s*)(\S)/, (_, whitespace, firstChar) => {
        return `${whitespace}${firstChar.toUpperCase()}`;
      })
    : text;

const getBackgroundUrl = (video) =>
  QWEN_Z_TWO_INPUT_IMAGES === video.modelType ||
  QWEN_Z_THREE_INPUT_IMAGES === video.modelType
    ? video?.input?.image?.url
    : video?.input?.image?.resizedImage?.url || video?.input?.image?.url;

const getThumbnailUrl = (video) =>
  video?.input?.image?.resizedImage?.url || video?.input?.image?.url;

const getVideoUrl = (video) =>
  video?.output?.video?.compressedUrl || video?.output?.video?.url;

const getAspectRatio = (video) => {
  const w = video?.input?.image?.resizedImage?.width ?? 1;
  const h = video?.input?.image?.resizedImage?.height ?? 1.5;
  return w / h;
};

const isSmallHeight = (video) => {
  const w = video?.input?.image?.resizedImage?.width ?? 1;
  const h = video?.input?.image?.resizedImage?.height ?? 1.5;
  return w > h;
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const SettingsOverlay = ({ openEditTrainModel }) => (
  <CheckboxContainer
    onClick={(e) => {
      e.stopPropagation();
      openEditTrainModel?.();
    }}
  >
    <LottieIcon
      animationData={settingsAnimation}
      trigger="hover"
      strokeColor="#fff"
      size={28}
    />
  </CheckboxContainer>
);

const SelectOverlay = ({ isSelected }) => (
  <CheckboxContainer>
    <LottieIcon
      animationData={checkboxAnimation}
      speed={2}
      trigger="toggle-reverse"
      isActive={isSelected}
      size={24}
    />
  </CheckboxContainer>
);

const MediaIcon = ({ isImage }) => (
  <IconWrapper>
    {isImage ? (
      <ImageIcon color="#ffffff" />
    ) : (
      <VideoRecorderIcon color="#ffffff" />
    )}
  </IconWrapper>
);

const CompletedImage = ({ url, onLoad }) => (
  <ImageElement src={url} onLoad={onLoad} />
);

const CompletedVideo = ({
  video,
  priority,
  shouldLoadVideo,
  isVideoLoaded,
  onLoad,
  videoRef,
}) => {
  const thumbnailUrl = getThumbnailUrl(video);
  const videoUrl = getVideoUrl(video);

  return (
    <>
      <CollectionThumbnail
        src={thumbnailUrl}
        alt={video.title}
        $loaded={isVideoLoaded}
        $blur={!isVideoLoaded}
        loading={priority ? "eager" : "lazy"}
      />
      {shouldLoadVideo && (
        <VideoElement
          ref={videoRef}
          controls={false}
          onLoadedData={onLoad}
          muted
          loop
          playsInline
          poster={thumbnailUrl}
          autoPlay
          preload="auto"
          $loaded={isVideoLoaded}
        >
          <source src={videoUrl} type="video/mp4" />
        </VideoElement>
      )}
    </>
  );
};

const FailedState = ({ video, isImage, retryLoading, onRetry }) => {
  return (
    <FailedOverlay>
      <LoadingImage src={getBackgroundUrl(video)} />

      <FailedContent>
        <FailedIconWrapper>
          <FailedIcon />
        </FailedIconWrapper>
        <FailedModelName>{formatModelName(video.name)}</FailedModelName>
        <FailedTitle>Generation Failed</FailedTitle>
        <FailedMessage>
          {isImage ? "Image" : "Video"} generation failed.
        </FailedMessage>
        <FailedMessage>Your credits have been refunded.</FailedMessage>
        <RetryButton onClick={onRetry} disabled={retryLoading}>
          {retryLoading ? (
            <l-line-spinner size="16" stroke="2" speed="1" color="#000" />
          ) : (
            "Retry"
          )}
        </RetryButton>
      </FailedContent>
    </FailedOverlay>
  );
};

const InProgressState = ({ video, isImage }) => {
  const small = isSmallHeight(video);
  const mediaLabel = isImage ? "Image" : "Video";
  const estimatedTime = isImage
    ? "1-2"
    : (VIDEO_RESOLUTION_TO_TIME[video?.output?.resolution] ?? "3-5");
  const { dots, loadingText } = useLoadingTitle(video?.loadingTexts);
  const formattedLoadingText = capitalizeFirstLetter(loadingText);

  return (
    <LoadingSpinnerBlur $show>
      <LottieIcon
        animationData={generatingAnimation}
        size={124}
        trigger="loop"
        style={LOTTIE_GENERATING_STYLE}
      />

      <LoadingImage src={getBackgroundUrl(video)} />

      <RunningText>
        {loadingText ? (
          <ShimmerTitleWrapper key={loadingText}>
            <ShimmerTitle>
              <ShimmerText>{formattedLoadingText}</ShimmerText>
              <DotsSpan>{dots}</DotsSpan>
            </ShimmerTitle>
          </ShimmerTitleWrapper>
        ) : (
          <RunningTitle>
            <span>Generating</span>
            <DotsSpan>{dots}</DotsSpan>
          </RunningTitle>
        )}

        {video.isFree ? (
          <FreeContent mediaLabel={mediaLabel} small={small} />
        ) : (
          <RunningMessage>
            {`It may take ${estimatedTime} minutes`}
            <br />
            to generate the {mediaLabel.toLowerCase()}.
          </RunningMessage>
        )}
      </RunningText>
    </LoadingSpinnerBlur>
  );
};

const FreeContent = ({ mediaLabel, small }) => {
  const navigate = useNavigate();

  return (
    <FreeMessageWrapper>
      <RunningMessage>
        {mediaLabel} generation may take 4 minutes or longer due to high
        computational demands and free-tier queue limits.
        <br />
        {!small && <br />}
        Upgrade your subscription for faster, higher-priority generation.
      </RunningMessage>
      <UpgradeButton
        $spacing={!small}
        onClick={(e) => {
          e.stopPropagation();
          navigate("/subscription");
        }}
      >
        Upgrade
      </UpgradeButton>
    </FreeMessageWrapper>
  );
};

const Footer = ({ video }) => (
  <VideoFooter>
    <DateLabel>{format(video.createdAt, "MM/dd/yyyy, h:mm a")}</DateLabel>
    <ModelName>{formatModelName(video.name)}</ModelName>
  </VideoFooter>
);

// ─── Main component ──────────────────────────────────────────────────────────

const CollectionItem = ({
  video,
  select,
  setSelectedCollections,
  selectedCollections,
  onCollectionItemClick,
  priority = false,
  trainPage,
  openEditTrainModel,
}) => {
  const dispatch = useDispatch();
  const videoRef = useRef(null);

  const autoplayEnabled = useSelector(selectAutoPlay);

  const { ref: autoplayRef, inView: isInAutoplayView } = useInView({
    threshold: 0.5,
    rootMargin: "0px",
  });

  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(priority);
  const [retryLoading, setRetryLoading] = useState(false);

  const { ref: containerRef, inView } = useInView({
    threshold: 0.01,
    rootMargin: "400px",
    triggerOnce: true,
  });

  // ── Derived state ──

  const isFailed = video.status === COLLECTION_STATUS.FAILED;
  const inProgress = video.status !== COLLECTION_STATUS.COMPLETED && !isFailed;
  const isImage = video?.output?.type === MEDIA_TYPE.IMAGE;
  const isContentReady = isImage ? isImageLoaded : isVideoLoaded;
  const isSelected = selectedCollections?.includes(video._id);
  const isClickable = (!inProgress && isContentReady) || select || isFailed;

  // ── Effects ──

  useEffect(() => {
    if (inView && !shouldLoadVideo && !inProgress) {
      const timer = setTimeout(
        () => setShouldLoadVideo(true),
        priority ? 0 : 100,
      );
      return () => clearTimeout(timer);
    }
  }, [inView, shouldLoadVideo, priority, inProgress]);

  // ── Handlers ──

  const handleRetry = useCallback(
    (e) => {
      e.stopPropagation();
      if (retryLoading) return;
      setRetryLoading(true);

      const seed = Math.floor(Math.random() * 10000000) + 1;
      const action =
        BLEND_MODEL === video.modelType
          ? regenerateBlendCollection({
              collectionId: video._id,
              seed: Math.floor(Math.random() * 10000000) + 1,
            })
          : isImageOutput(video.modelType)
            ? regenerateImageCollection({ collectionId: video._id, seed })
            : regenerateCollection({ collectionId: video._id, seed });

      dispatch(action)
        .unwrap()
        .then(() => {
          toast.success("Regeneration started...");

          setRetryLoading(false);
        })
        .catch((err) => {
          toast.error(err);
          setRetryLoading(false);
        });
    },
    [dispatch, retryLoading, video._id, video.modelType],
  );

  const handleClick = useCallback(() => {
    if (select && !inProgress) {
      setSelectedCollections((prev) =>
        prev.includes(video._id)
          ? prev.filter((id) => id !== video._id)
          : [...prev, video._id],
      );
      return;
    }

    if (isContentReady && !inProgress && onCollectionItemClick) {
      onCollectionItemClick(video);
    }
  }, [
    select,
    isContentReady,
    inProgress,
    video,
    onCollectionItemClick,
    setSelectedCollections,
  ]);

  const ensureInlinePlayback = useCallback(() => {
    const element = videoRef.current;
    if (!element) return;

    element.muted = true;
    element.defaultMuted = true;
    element.playsInline = true;
    element.loop = true;
    element.preload = "none";
    element.setAttribute("muted", "");
    element.setAttribute("playsinline", "");
    element.setAttribute("webkit-playsinline", "");
    element.setAttribute("loop", "");
  }, []);

  useEffect(() => {
    if (!videoRef.current || isImage) return;

    ensureInlinePlayback();

    const shouldPlay = autoplayEnabled && isInAutoplayView;

    if (shouldPlay) {
      const playPromise = videoRef.current.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch((error) => {
          console.log(error);
        });
      }
    } else {
      videoRef.current.pause();
    }
  }, [ensureInlinePlayback, autoplayEnabled, isInAutoplayView, isImage]);

  // ── Render ──

  return (
    <CollectionContainer
      ref={(el) => {
        containerRef(el);
        autoplayRef(el);
      }}
      $aspectRatio={getAspectRatio(video)}
      $clickable={isClickable}
      $inProgress={inProgress}
      $hasVideoUrl={
        isVideoLoaded && video.status === COLLECTION_STATUS.COMPLETED
      }
      onClick={handleClick}
    >
      {trainPage && <SettingsOverlay openEditTrainModel={openEditTrainModel} />}
      {!inProgress && select && <SelectOverlay isSelected={isSelected} />}

      <MediaIcon isImage={isImage} />

      {!inProgress && !isFailed && isImage && (
        <CompletedImage
          url={video.output.image.url}
          onLoad={() => setIsImageLoaded(true)}
        />
      )}

      {!inProgress && !isFailed && !isImage && (
        <CompletedVideo
          video={video}
          priority={priority}
          shouldLoadVideo={shouldLoadVideo}
          isVideoLoaded={isVideoLoaded}
          onLoad={() => setIsVideoLoaded(true)}
          videoRef={videoRef}
        />
      )}

      {isFailed && (
        <FailedState
          video={video}
          isImage={isImage}
          retryLoading={retryLoading}
          onRetry={handleRetry}
        />
      )}

      {inProgress && <InProgressState video={video} isImage={isImage} />}

      {!inProgress && !isFailed && <Footer video={video} />}
    </CollectionContainer>
  );
};

export default React.memo(CollectionItem);
